import { useReactFlow } from '@xyflow/react'
import { NodeProps } from '@xyflow/react'
import { HelpCircle, Search, RefreshCw, Eye, FileText, Copy, Download, Filter, Settings } from 'lucide-react'
import React, { useState, useEffect, useRef, useMemo, memo } from 'react'
import { useParams } from 'react-router'
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import { useUser } from '@/context/UserContext'
import useDataSettings from '@/hooks/useDataSettings'
import { 
  useAICommandState, 
  createAICommand, 
  createNodeCommandPayload, 
  isCommandComplete, 
  isCommandError, 
  AICommandType, 
  AICommandStatus 
} from '@/utils/aiCommandUtils'
import styles from '@/styles/NodeDomainKnowledge.module.css'
import MarkdownRenderer from '../../markdown-renderer/MarkdownRenderer'
import NodeBaseActions from '../base/base-actions/NodeBaseActions'
import NodeBaseContainer from '../base/base-container/NodeBaseContainer'
import NodeConfigurationDialog, { NodeConfiguration } from '../../dialogs/configuration-dialog/NodeConfigurationDialog'
import NodeDataSettingsDialog from '../../dialogs/data-settings-dialog/NodeDataSettingsDialog'

interface NodeDomainKnowledgeData {
  label?: string
  isMinimized?: boolean
  isLocked?: boolean
  statusMessage?: string
  domain?: string
  subdomain?: string
  domainKnowledge?: {
    json: any
    markdown: string
    sources: string
  }
  contentMarkdown?: string
  userId?: string
  projectId?: string
  documentRequest?: {
    document: string
    processing: boolean
    processedAt: string
    timestamp: number
    'sysagent-cmd': string
    'sysagent-response': string
    [key: string]: any
  }
  assignedEmails?: string[] | string
  dataSettings?: any
}

const NodeDomainKnowledge: React.FC<NodeProps & { data: NodeDomainKnowledgeData }> = (props) => {
  const { id, data, selected } = props
  const { projectId } = useParams()
  const { updateNodeData } = useReactFlow()
  const user = useUser()
  
  // Local state management - use data as source of truth, local state for UI only
  const [domain, setDomain] = useState(data.domain || '')
  const [subdomain, setSubdomain] = useState(data.subdomain || '')
  const [statusMessage, setStatusMessage] = useState(data.statusMessage || '')
  const [isLocked, setIsLocked] = useState(data.isLocked || false)
  const [isMinimized, setIsMinimized] = useState(data.isMinimized || false)
  const [isEditingTitle, setIsEditingTitle] = useState(false)
  const [editingTitleValue, setEditingTitleValue] = useState(data.label || '')
  const [activeTab, setActiveTab] = useState<'input' | 'knowledge' | 'sources' | 'markdown'>('input')
  const [showHelpDialog, setShowHelpDialog] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [itemToDelete, setItemToDelete] = useState(null)
  const [isGathering, setIsGathering] = useState(false)
  const [assignedEmails, setAssignedEmails] = useState<string[]>(
    Array.isArray(data.assignedEmails) ? data.assignedEmails : 
    (data.assignedEmails ? data.assignedEmails.trim().split(/\s+/).filter(Boolean) : [])
  )
  const [isDataSettingsDialogVisible, setIsDataSettingsDialogVisible] = useState(false)

  // --- Configuration dialog state ---
  const [isConfigDialogVisible, setIsConfigDialogVisible] = useState(false)
  const [nodeConfig, setNodeConfig] = useState<NodeConfiguration>(
    data.configuration || { isAutomated: false, modifierprompt: '' }
  )

  // Keep nodeConfig in sync with collaborative state
  useEffect(() => {
    if (data.configuration) setNodeConfig(data.configuration)
  }, [data.configuration]) // <-- update dependency to always sync latest

  const titleInputRef = useRef<HTMLInputElement>(null)

  // --- Wheel scroll event refs for scrollable areas ---
  const contentRefs = useRef<{
    input: HTMLDivElement | null
    knowledge: HTMLDivElement | null
    sources: HTMLDivElement | null
  }>({
    input: null,
    knowledge: null,
    sources: null,
  })

  // Monitor command state
  const domainKnowledgeState = useAICommandState(data.documentRequest, AICommandType.DOMAINKNOWLEDGE_PROCESS)
  
  const currentStatus = isGathering 
    ? 'loading'
    : data.documentRequest && isCommandComplete(data.documentRequest, AICommandType.DOMAINKNOWLEDGE_PROCESS)
    ? 'success'
    : data.documentRequest && isCommandError(data.documentRequest, AICommandType.DOMAINKNOWLEDGE_PROCESS)
    ? 'error'
    : ''

  // Sync node data to React Flow - single source of truth
  const syncNodeData = (newData: Partial<NodeDomainKnowledgeData>) => {
    const updatedData = { ...data, ...newData }
    updateNodeData(id, updatedData)
  }

  // ✅ CORRECT: Listen to collaborative state changes and update local state
  useEffect(() => {
    if (data.domain !== undefined) setDomain(data.domain)
    if (data.subdomain !== undefined) setSubdomain(data.subdomain)
    if (data.statusMessage !== undefined) setStatusMessage(data.statusMessage)
    if (data.isMinimized !== undefined) setIsMinimized(data.isMinimized)
    if (data.isLocked !== undefined) setIsLocked(data.isLocked)
    if (data.label !== undefined) setEditingTitleValue(data.label)
    if (data.assignedEmails !== undefined)
      setAssignedEmails(Array.isArray(data.assignedEmails) ? data.assignedEmails : 
        (data.assignedEmails ? data.assignedEmails.trim().split(/\s+/).filter(Boolean) : []))
    if (data.configuration) setNodeConfig(data.configuration)
  }, [])

  // ✅ CORRECT: Only set project ID once without calling syncNodeData in useEffect
  useEffect(() => {
    if (projectId && !data.projectId) {
      // Only sync if we don't already have a project ID
      syncNodeData({ projectId })
    }
  }, [projectId]) // Remove data.projectId dependency to avoid loops

  useEffect(() => {
    setIsGathering(domainKnowledgeState.isProcessing)
  }, [domainKnowledgeState.isProcessing])

  // Wheel event listener management (let browser handle everything, no stopPropagation, no preventDefault)
  useEffect(() => {
    // Remove all custom wheel handling: let browser handle scrolling and selection natively
    // This matches native scrollable area behavior and allows text selection, copy, and scroll
    return () => {}
  }, [isMinimized, activeTab])

  // Restore wheel event handler to allow scroll and panning at edge, but do not block selection.
  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      // Only handle if not actively selecting text or dragging
      const selection = window.getSelection()
      if (
        (selection && selection.type === 'Range') ||
        (e.buttons && e.buttons !== 0)
      ) {
        return
      }
      const el = e.currentTarget as HTMLElement
      const { scrollTop, scrollHeight, clientHeight } = el
      const delta = e.deltaY
      const atTop = scrollTop <= 0
      const atBottom = Math.abs(scrollTop + clientHeight - scrollHeight) < 2

      // Only stop propagation if actually scrolling (not at edge)
      if (
        (delta < 0 && !atTop) ||
        (delta > 0 && !atBottom)
      ) {
        // Let browser handle scroll, just stop propagation to prevent node panning
        e.stopPropagation()
      }
      // If at edge, let event bubble for node panning
    }

    const elements = Object.values(contentRefs.current).filter(Boolean) as HTMLElement[]
    elements.forEach((el) => {
      el.addEventListener('wheel', handleWheel, { passive: false })
    })
    return () => {
      elements.forEach((el) => {
        el.removeEventListener('wheel', handleWheel)
      })
    }
  }, [isMinimized, activeTab])

  // Node action handlers - ✅ CORRECT: Only call syncNodeData, don't update local state
  const handleLock = () => {
    const newState = !isLocked
    syncNodeData({ isLocked: newState })
  }

  const handleMinimize = () => {
    const newState = !isMinimized
    syncNodeData({ isMinimized: newState })
  }

  const confirmDelete = (item) => {
    setItemToDelete(item)
    setShowDeleteDialog(true)
  }

  const handleDeleteConfirmed = () => {
    if (itemToDelete && (window as any).__flowDeleteNode) {
      (window as any).__flowDeleteNode(id)
    }
    setShowDeleteDialog(false)
    setItemToDelete(null)
  }

  const handleDuplicate = () => {
    if ((window as any).__flowDuplicateNode) {
      (window as any).__flowDuplicateNode(id)
    }
  }

  const handleTitleSave = () => {
    syncNodeData({ label: editingTitleValue })
    setIsEditingTitle(false)
  }

  const handleTitleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleTitleSave()
    } else if (e.key === 'Escape') {
      setIsEditingTitle(false)
      setEditingTitleValue(data.label || '')
    }
  }

  useEffect(() => {
    if (isEditingTitle && titleInputRef.current) {
      titleInputRef.current.focus()
      titleInputRef.current.select()
    }
  }, [isEditingTitle])

  // Input handlers - ✅ CORRECT: Update local state immediately, sync on blur/change
  const handleDomainChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDomain(e.target.value)
  }

  const handleDomainBlur = () => {
    syncNodeData({ domain })
  }

  const handleSubdomainChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSubdomain(e.target.value)
  }

  const handleSubdomainBlur = () => {
    syncNodeData({ subdomain })
  }

  // Gather domain knowledge
  const handleGatherKnowledge = async () => {
    if (!domain.trim()) {
      syncNodeData({ statusMessage: 'Error: Please enter a domain.' })
      return
    }

    const effectiveUserId = user?.id || user?.email
    if (!effectiveUserId) {
      syncNodeData({ statusMessage: 'Error: User information missing. Please log in.' })
      return
    }

    const currentProjectId = data.projectId || projectId
    if (!currentProjectId) {
      syncNodeData({ statusMessage: 'Error: No project ID available. Please reload the page.' })
      return
    }

    syncNodeData({ statusMessage: 'Gathering domain knowledge...' })

    try {
      // Build the prompt
      const prompt = `Domain: ${domain}\nSubdomain: ${subdomain || 'General'}`

      // Prepare the command to be processed by DomainKnowledgeProcessor
      const documentRequest = createAICommand(
        AICommandType.DOMAINKNOWLEDGE_PROCESS,
        AICommandStatus.STARTED,
        createNodeCommandPayload(id, {
          domain,
          subdomain,
          prompt,
          userId: effectiveUserId,
          projectId: currentProjectId,
          document: 'domain-knowledge'
        })
      )

      syncNodeData({
        documentRequest,
        domain,
        subdomain,
        userId: effectiveUserId
      })

      setActiveTab('knowledge')
    } catch (error) {
      console.error('Error preparing domain knowledge gathering:', error)
      syncNodeData({ statusMessage: `Error: ${error.message || 'Unknown error'}` })
    }
  }

  // Copy domain knowledge JSON to clipboard
  const copyKnowledgeJson = () => {
    if (data.domainKnowledge?.json) {
      navigator.clipboard.writeText(JSON.stringify(data.domainKnowledge.json, null, 2))
      syncNodeData({ statusMessage: 'Domain knowledge JSON copied to clipboard' })
      setTimeout(() => {
        syncNodeData({ statusMessage: '' })
      }, 3000)
    }
  }

  // Copy domain knowledge markdown to clipboard
  const copyKnowledgeMarkdown = () => {
    if (data.domainKnowledge?.markdown) {
      navigator.clipboard.writeText(data.domainKnowledge.markdown)
      syncNodeData({ statusMessage: 'Domain knowledge markdown copied to clipboard' })
      setTimeout(() => {
        syncNodeData({ statusMessage: '' })
      }, 3000)
    }
  }

    const copyKnowledgeSourcesandAssumptions = () => {
    if (data.domainKnowledge?.markdown) {
      navigator.clipboard.writeText(data.domainKnowledge.sources)
      syncNodeData({ statusMessage: 'Domain knowledge sources and assumptions copied to clipboard' })
      setTimeout(() => {
        syncNodeData({ statusMessage: '' })
      }, 3000)
    }
  }

  // Process the domain knowledge result when the command completes
  useEffect(() => {
    if (!data.documentRequest) return

    if (isCommandComplete(data.documentRequest, AICommandType.DOMAINKNOWLEDGE_PROCESS)) {
      if (data.documentRequest.domainKnowledge) {
        setTimeout(() => {
          syncNodeData({
            domainKnowledge: data.documentRequest.domainKnowledge,
            contentMarkdown: data.documentRequest.formattedContent || data.documentRequest.domainKnowledge.markdown,
            statusMessage: 'Domain knowledge gathered successfully',
            documentRequest: undefined,
          })
        }, 100)
        setActiveTab('knowledge')
      }
    } else if (isCommandError(data.documentRequest, AICommandType.DOMAINKNOWLEDGE_PROCESS)) {
      const errorMsg = data.documentRequest.error || 'Unknown error'
      setTimeout(() => {
        syncNodeData({ 
          statusMessage: `Error gathering domain knowledge: ${errorMsg}`,
          documentRequest: undefined,
        })
      }, 100)
    }
  }, [data.documentRequest])

  // Compute status for NodeBaseContainer using useMemo
  const nodeStatus = useMemo(() => {
    if (!data?.statusMessage) return "initial";
    const lowerMessage = data.statusMessage.toLowerCase();
    if (lowerMessage.includes("loading")) return "loading";
    if (lowerMessage.includes("error")) return "error";
    if (lowerMessage.includes("complete") || lowerMessage.includes("successful")) return "success";
    return "initial";
  }, [data?.statusMessage]);

  // --- Data Settings logic (outgoing only) ---
  // Expose markdown and sources as outgoing fields
  const outgoingFields = {
    domain: {
      value: domain,
      enabled: true,
      label: 'Domain',
      description: 'The domain being analyzed',
      permissions: { readable: true, writable: false },
    },
    subdomain: {
      value: subdomain,
      enabled: true,
      label: 'Subdomain',
      description: 'The subdomain being analyzed',
      permissions: { readable: true, writable: false },
    },
    domainKnowledgeMarkdown: {
      value: data.domainKnowledge?.markdown || '',
      enabled: !!data.domainKnowledge?.markdown,
      label: 'Domain Knowledge Markdown',
      description: 'Domain knowledge in markdown format',
      permissions: { readable: true, writable: false },
    },
    domainKnowledgeSources: {
      value: data.domainKnowledge?.sources || '',
      enabled: !!data.domainKnowledge?.sources,
      label: 'Domain Knowledge Sources',
      description: 'Domain knowledge sources and assumptions',
      permissions: { readable: true, writable: false },
    },
  }

  // Use useDataSettings for outgoing/exposed data only
  const {
    dataSettings,
    setDataSettings,
    handleDataSettingsChange,
    handleOpenDataSettingsDialog,
    prepareExposedDataFields,
  } = useDataSettings(
    id,
    data,
    (newData) => syncNodeData(newData),
    false, // showDebugInfo
    '', // context not relevant here
    new Map(), // sections not relevant
    '', // contentMarkdown not relevant
    setIsDataSettingsDialogVisible
  )

  // Always keep outgoing/exposedData up to date
  useEffect(() => {
    const newDataSettings = {
      ...dataSettings,
      inputLinks: [],
      exposedData: outgoingFields,
      availableNodes: [],
      outputLinks: dataSettings?.outputLinks || [],
      autoSync: false,
    }
    if (JSON.stringify(dataSettings?.exposedData) !== JSON.stringify(outgoingFields)) {
      setDataSettings(newDataSettings)
      syncNodeData({ dataSettings: newDataSettings })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    data.domain,
    data.subdomain,
    data.domainKnowledge,
  ])

  // Add DataSettings and Config buttons to custom actions
  const customButtons = [
    {
      id: 'data-settings',
      onClick: handleOpenDataSettingsDialog,
      icon: <Filter size={16} />,
      tooltip: 'Data connection settings',
    },
    {
      id: 'config',
      onClick: () => setIsConfigDialogVisible(true),
      icon: <Settings size={16} />,
      tooltip: 'Node configuration',
    },
  ]

  const [showDomainDropdown, setShowDomainDropdown] = useState(false)
  const [showSubdomainDropdown, setShowSubdomainDropdown] = useState(false)
  const domainSuggestions = useMemo(() => getDomainSuggestions(domain), [domain])
  const subdomainSuggestions = useMemo(() => getSubdomainSuggestions(domain, subdomain), [domain, subdomain])

  return (
    <NodeBaseContainer
      id={id}
      data={data}
      resizable={!isLocked}
      isProcessing={isGathering} 
      status={nodeStatus}
      nodeTitle={editingTitleValue || data.label || 'Domain Knowledge'}
      onTitleChange={(newTitle) => {
        syncNodeData({ label: newTitle })
      }}
      assignedEmails={Array.isArray(assignedEmails) ? assignedEmails : (assignedEmails ? assignedEmails.trim().split(/\s+/).filter(Boolean) : [])}
      onAssignUsersClick={() => {
        const emailsStr = window.prompt('Enter emails separated by space', Array.isArray(assignedEmails) ? assignedEmails.join(' ') : (assignedEmails || ''))
        if (emailsStr !== null) {
          const emailsArr = emailsStr.trim().split(/\s+/).filter(Boolean)
          syncNodeData({ assignedEmails: emailsArr })
        }
      }}
      selected={selected}
      customButtons={customButtons}
    >
      <div className="flex flex-col h-full w-full bg-white rounded-lg shadow">
        <div className="flex justify-between items-center px-2 py-3">
          <NodeBaseActions
            nodeId={id}
            onDelete={confirmDelete}
            onDuplicate={handleDuplicate}
            onLock={handleLock}
            onUnlock={isLocked}
            onMinimize={handleMinimize}
            onMaximize={isMinimized}
          />
          <div className="flex items-center space-x-2">
            <button
              className="px-2 py-1 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition-colors"
              onClick={() => setShowHelpDialog(true)}
            >
              <HelpCircle className="h-4 w-4" />
            </button>
          </div>
        </div>

        {!isMinimized && (
          // Make this container flex-grow and min-h-0 for proper flexbox scrolling
          <div className="flex flex-col flex-grow min-h-0 px-2 pb-2 space-y-2">
            <Tabs
              defaultValue="input"
              value={activeTab}
              onValueChange={(value) => setActiveTab(value as 'input' | 'knowledge' | 'sources' | 'markdown')}
              className="w-full flex flex-col flex-grow min-h-0"
            >
              <TabsList className="grid grid-cols-4">
                <TabsTrigger value="input">Input</TabsTrigger>
                <TabsTrigger value="knowledge">Domain Information</TabsTrigger>
                <TabsTrigger value="sources">Sources & Assumptions</TabsTrigger>
                <TabsTrigger value="markdown">Markdown</TabsTrigger>
              </TabsList>

              {/* Make each TabsContent flex-grow and min-h-0 for scrollable content */}
              <TabsContent value="input" className="border-none p-0 mt-2 flex flex-col flex-grow min-h-0">
                <div
                  ref={el => (contentRefs.current.input = el)}
                  className="flex flex-col flex-grow min-h-0 space-y-4 scrollable-content nodrag"
                  style={{ overflowY: 'auto', userSelect: 'text' }}
                >
                  <div className="border rounded-md p-3 nowheel">
                    <div className="space-y-3">
                      {/* --- Domain Autocomplete --- */}
                      <div style={{ position: 'relative' }}>
                        <label className="block text-sm font-medium mb-1">Domain:</label>
                        <Input
                          placeholder="e.g., Information Technology, Healthcare, Finance"
                          value={domain}
                          onChange={e => {
                            setDomain(e.target.value)
                            setShowDomainDropdown(true)
                            setSubdomain('') // Reset subdomain if domain changes
                          }}
                          onFocus={() => setShowDomainDropdown(true)}
                          onBlur={e => {
                            setTimeout(() => setShowDomainDropdown(false), 150)
                            handleDomainBlur()
                          }}
                          className="nodrag"
                          disabled={isLocked || isGathering}
                          autoComplete="off"
                        />
                        {showDomainDropdown && domainSuggestions.length > 0 && (
                          <div
                            style={{
                              position: 'absolute',
                              zIndex: 10,
                              background: 'white',
                              border: '1px solid #e5e7eb',
                              borderRadius: 4,
                              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                              width: '100%',
                              maxHeight: 180,
                              overflowY: 'auto',
                            }}
                          >
                            {domainSuggestions.map((d, idx) => (
                              <div
                                key={idx}
                                style={{
                                  padding: '6px 12px',
                                  cursor: 'pointer',
                                  background: d.domain === domain ? '#f3f4f6' : 'white',
                                }}
                                onMouseDown={e => {
                                  e.preventDefault()
                                  setDomain(d.domain)
                                  setShowDomainDropdown(false)
                                }}
                              >
                                {d.domain}
                              </div>
                            ))}
                          </div>
                        )}
                        {/* Show NAICS/NACE codes if domain is selected */}
                        {DOMAIN_LIST.find(d => d.domain === domain) && (
                          <div className="text-xs text-gray-500 mt-1">
                            NAICS: {DOMAIN_LIST.find(d => d.domain === domain)?.naics.join(', ')} | NACE: {DOMAIN_LIST.find(d => d.domain === domain)?.nace.join(', ')}
                          </div>
                        )}
                      </div>
                      {/* --- Subdomain Autocomplete --- */}
                      <div style={{ position: 'relative' }}>
                        <label className="block text-sm font-medium mb-1">Subdomain (Optional):</label>
                        <Input
                          placeholder="e.g., Game Development, Machine Learning, Web Development"
                          value={subdomain}
                          onChange={e => {
                            setSubdomain(e.target.value)
                            setShowSubdomainDropdown(true)
                          }}
                          onFocus={() => setShowSubdomainDropdown(true)}
                          onBlur={e => {
                            setTimeout(() => setShowSubdomainDropdown(false), 150)
                            handleSubdomainBlur()
                          }}
                          className="nodrag"
                          disabled={isLocked || isGathering}
                          autoComplete="off"
                        />
                        {showSubdomainDropdown && subdomainSuggestions.length > 0 && (
                          <div
                            style={{
                              position: 'absolute',
                              zIndex: 10,
                              background: 'white',
                              border: '1px solid #e5e7eb',
                              borderRadius: 4,
                              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                              width: '100%',
                              maxHeight: 180,
                              overflowY: 'auto',
                            }}
                          >
                            {subdomainSuggestions.map((s, idx) => (
                              <div
                                key={idx}
                                style={{
                                  padding: '6px 12px',
                                  cursor: 'pointer',
                                  background: s === subdomain ? '#f3f4f6' : 'white',
                                }}
                                onMouseDown={e => {
                                  e.preventDefault()
                                  setSubdomain(s)
                                  setShowSubdomainDropdown(false)
                                }}
                              >
                                {s}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex">
                    <Button
                      className="flex-1"
                      onClick={handleGatherKnowledge}
                      disabled={isLocked || isGathering || !domain.trim()}
                    >
                      {isGathering ? (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                          Gathering Knowledge...
                        </>
                      ) : (
                        <>
                          <Search className="h-4 w-4 mr-2" />
                          Gather Domain Knowledge
                        </>
                      )}
                    </Button>
                  </div>

                  {/* Status Message */}
                  {statusMessage && (
                    <div className={`text-sm p-2 rounded ${
                      currentStatus === 'error' 
                        ? 'text-red-700 bg-red-50 border border-red-200' 
                        : currentStatus === 'success'
                        ? 'text-green-700 bg-green-50 border border-green-200'
                        : 'text-blue-700 bg-blue-50 border border-blue-200'
                    }`}>
                      {statusMessage}
                    </div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="knowledge" className="border-none p-0 mt-2 flex flex-col flex-grow min-h-0">
                <div
                  ref={el => (contentRefs.current.knowledge = el)}
                  className="flex flex-col flex-grow min-h-0 space-y-4 scrollable-content nodrag"
                  style={{ overflowY: 'auto', userSelect: 'text' }}
                >
                  <div className="border rounded-md p-3 flex flex-col flex-grow min-h-0 nowheel">
                    {/* --- Industry Codes --- */}
                    {data.domainKnowledge?.json?.NAICS_NACE_Codes && (
                      <div className="mb-4">
                        <h4 className="font-semibold mb-2">Industry Codes</h4>
                        <div className="grid grid-cols-2 gap-2 mb-2">
                          <div>
                            <Badge variant="outline">NAICS</Badge>
                            <p className="text-sm mt-1">{data.domainKnowledge.json.NAICS_NACE_Codes.NAICS}</p>
                          </div>
                          <div>
                            <Badge variant="outline">NACE</Badge>
                            <p className="text-sm mt-1">{data.domainKnowledge.json.NAICS_NACE_Codes.NACE}</p>
                          </div>
                        </div>
                        {data.domainKnowledge.json.NAICS_NACE_Codes.AlignmentRationale && (
                          <div className="text-xs text-gray-600 mt-2">
                            <span className="font-semibold">Alignment Rationale:</span> {data.domainKnowledge.json.NAICS_NACE_Codes.AlignmentRationale}
                          </div>
                        )}
                      </div>
                    )}

                    {/* --- Classification --- */}
                    <div className="mb-4">
                      <h4 className="font-semibold mb-2">Classification</h4>
                      <div className="space-y-1">
                        <p><strong>Domain:</strong> {data.domainKnowledge?.json?.Domain}</p>
                        <p><strong>Subdomain:</strong> {data.domainKnowledge?.json?.Subdomain}</p>
                      </div>
                    </div>

                    {/* --- Domain Specific Terminology --- */}
                    {Array.isArray(data.domainKnowledge?.json?.DomainSpecificTerminology) && (
                      <div className="mb-4">
                        <h4 className="font-semibold mb-2">Domain Specific Terminology</h4>
                        <div className="flex flex-wrap gap-2 text-xs">
                          {data.domainKnowledge.json.DomainSpecificTerminology
                            .slice()
                            .sort((a, b) => a.localeCompare(b))
                            .map((term, idx) => (
                              <span key={idx} className="bg-blue-50 border border-blue-200 rounded px-2 py-1">{term}</span>
                            ))}
                        </div>
                      </div>
                    )}

                    {/* --- Data Types --- */}
                    {data.domainKnowledge?.json?.DataTypes && (
                      <div className="mb-4">
                        <h4 className="font-semibold mb-2">Data Types</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <h5 className="font-medium mb-1">Gathered</h5>
                            <ul className="list-disc ml-5 text-sm space-y-1">
                              {(data.domainKnowledge.json.DataTypes.Gathered || []).map((item, idx) => (
                                <li key={idx}>{item}</li>
                              ))}
                            </ul>
                          </div>
                          <div>
                            <h5 className="font-medium mb-1">Produced</h5>
                            <ul className="list-disc ml-5 text-sm space-y-1">
                              {(data.domainKnowledge.json.DataTypes.Produced || []).map((item, idx) => (
                                <li key={idx}>{item}</li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* --- Desired Insights --- */}
                    {Array.isArray(data.domainKnowledge?.json?.DesiredInsights) && (
                      <div className="mb-4">
                        <h4 className="font-semibold mb-2">Desired Insights</h4>
                        <ul className="list-disc ml-5 text-sm space-y-1">
                          {data.domainKnowledge.json.DesiredInsights.map((item, idx) => (
                            <li key={idx}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* --- Data To Information Methods --- */}
                    {Array.isArray(data.domainKnowledge?.json?.DataToInformationMethods) && (
                      <div className="mb-4">
                        <h4 className="font-semibold mb-2">Data To Information Methods</h4>
                        <ul className="list-disc ml-5 text-sm space-y-1">
                          {data.domainKnowledge.json.DataToInformationMethods.map((item, idx) => (
                            <li key={idx}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* --- Knowledge --- */}
                    {data.domainKnowledge?.json?.Knowledge && (
                      <div className="mb-4">
                        <h4 className="font-semibold mb-2">Knowledge</h4>
                        <div className="mb-2 text-sm">{data.domainKnowledge.json.Knowledge.Description}</div>
                        <div className="mb-1">
                          <span className="font-medium">Input:</span> {data.domainKnowledge.json.Knowledge.Input}
                        </div>
                        <div className="mb-1">
                          <span className="font-medium">Output:</span> {data.domainKnowledge.json.Knowledge.Output}
                        </div>
                        <div className="mb-1">
                          <span className="font-medium">Examples:</span>
                          <ul className="list-disc ml-5 text-sm space-y-1">
                            {(data.domainKnowledge.json.Knowledge.Examples || []).map((ex, idx) => (
                              <li key={idx}>{ex}</li>
                            ))}
                          </ul>
                        </div>
                        <div>
                          <span className="font-medium">Processes:</span>
                          <ul className="list-disc ml-5 text-sm space-y-1">
                            {(data.domainKnowledge.json.Knowledge.Processes || []).map((proc, idx) => (
                              <li key={idx}>{proc}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    )}

                    {/* --- Wisdom --- */}
                    {data.domainKnowledge?.json?.Wisdom && (
                      <div className="mb-4">
                        <h4 className="font-semibold mb-2">Wisdom</h4>
                        <div className="mb-2 text-sm">{data.domainKnowledge.json.Wisdom.Description}</div>
                        <div className="mb-1">
                          <span className="font-medium">Input:</span> {data.domainKnowledge.json.Wisdom.Input}
                        </div>
                        <div className="mb-1">
                          <span className="font-medium">Output:</span> {data.domainKnowledge.json.Wisdom.Output}
                        </div>
                        <div className="mb-1">
                          <span className="font-medium">Examples:</span>
                          <ul className="list-disc ml-5 text-sm space-y-1">
                            {(data.domainKnowledge.json.Wisdom.Examples || []).map((ex, idx) => (
                              <li key={idx}>{ex}</li>
                            ))}
                          </ul>
                        </div>
                        <div>
                          <span className="font-medium">Practices:</span>
                          <ul className="list-disc ml-5 text-sm space-y-1">
                            {(data.domainKnowledge.json.Wisdom.Practices || []).map((proc, idx) => (
                              <li key={idx}>{proc}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    )}

                    {/* --- Synthesis --- */}
                    {data.domainKnowledge?.json?.Synthesis && (
                      <div className="mb-4">
                        <h4 className="font-semibold mb-2">Synthesis</h4>
                        <div className="mb-2 text-sm">{data.domainKnowledge.json.Synthesis.Description}</div>
                        <div className="mb-1">
                          <span className="font-medium">Input:</span> {data.domainKnowledge.json.Synthesis.Input}
                        </div>
                        <div className="mb-1">
                          <span className="font-medium">Output:</span> {data.domainKnowledge.json.Synthesis.Output}
                        </div>
                        <div className="mb-1">
                          <span className="font-medium">Examples:</span>
                          <ul className="list-disc ml-5 text-sm space-y-1">
                            {(data.domainKnowledge.json.Synthesis.Examples || []).map((ex, idx) => (
                              <li key={idx}>{ex}</li>
                            ))}
                          </ul>
                        </div>
                        <div>
                          <span className="font-medium">Practices:</span>
                          <ul className="list-disc ml-5 text-sm space-y-1">
                            {(data.domainKnowledge.json.Synthesis.Practices || []).map((proc, idx) => (
                              <li key={idx}>{proc}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    )}

                    {/* --- Deliverables --- */}
                    {data.domainKnowledge?.json?.Deliverables && (
                      <div className="mb-4">
                        <h4 className="font-semibold mb-2">Deliverables</h4>
                        <div className="mb-2 text-sm">{data.domainKnowledge.json.Deliverables.Description}</div>
                        <div className="mb-1">
                          <span className="font-medium">Input:</span> {data.domainKnowledge.json.Deliverables.Input}
                        </div>
                        <div className="mb-1">
                          <span className="font-medium">Output:</span> {data.domainKnowledge.json.Deliverables.Output}
                        </div>
                        <div className="mb-1">
                          <span className="font-medium">Examples:</span>
                          <ul className="list-disc ml-5 text-sm space-y-1">
                            {(data.domainKnowledge.json.Deliverables.Examples || []).map((ex, idx) => (
                              <li key={idx}>{ex}</li>
                            ))}
                          </ul>
                        </div>
                        <div>
                          <span className="font-medium">Targets:</span>
                          <ul className="list-disc ml-5 text-sm space-y-1">
                            {(data.domainKnowledge.json.Deliverables.Targets || []).map((proc, idx) => (
                              <li key={idx}>{proc}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    )}

                    {/* --- Standards --- */}
                    {data.domainKnowledge?.json?.Standards && (
                      <div className="mb-4">
                        <h4 className="font-semibold mb-2">Standards</h4>
                        <div className="mb-2">
                          <span className="font-medium">Global Systems:</span>
                          <ul className="list-disc ml-5 text-sm space-y-1">
                            {(data.domainKnowledge.json.Standards.GlobalSystems || []).map((item, idx) => (
                              <li key={idx}>{item}</li>
                            ))}
                          </ul>
                        </div>
                        <div className="mb-2">
                          <span className="font-medium">Country Specific Systems:</span>
                          <ul className="list-disc ml-5 text-sm space-y-1">
                            {(data.domainKnowledge.json.Standards.CountrySpecificSystems || []).map((item, idx) => (
                              <li key={idx}>{item}</li>
                            ))}
                          </ul>
                        </div>
                        <div>
                          <span className="font-medium">Specialized Regional Systems:</span>
                          <ul className="list-disc ml-5 text-sm space-y-1">
                            {(data.domainKnowledge.json.Standards.SpecializedRegionalSystems || []).map((item, idx) => (
                              <li key={idx}>{item}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    )}

                    {/* --- Level Definitions --- */}
                    {Array.isArray(data.domainKnowledge?.json?.LevelDefinitions) && (
                      <div className="mb-4">
                        <h4 className="font-semibold mb-2">Level Definitions</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {data.domainKnowledge.json.LevelDefinitions.map((level, idx) => (
                            <div key={idx} className="border rounded p-3 bg-gray-50">
                              <div className="font-medium mb-1">
                                Level {level.Level}: {level.Name}
                              </div>
                              <div className="text-xs mb-1">{level.BriefDescription}</div>
                              <div className="text-xs"><span className="font-semibold">Input:</span> {level.Input}</div>
                              <div className="text-xs"><span className="font-semibold">Output:</span> {level.Output}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="sources" className="border-none p-0 mt-2 flex flex-col flex-grow min-h-0">
                <div
                  ref={el => (contentRefs.current.sources = el)}
                  className="flex flex-col flex-grow min-h-0 space-y-4 scrollable-content nodrag"
                  style={{ overflowY: 'auto', userSelect: 'text' }}
                >
                  <div className="border rounded-md p-3 flex flex-col flex-grow min-h-0">
                    <div className="flex justify-between items-center mb-2">
                      <h3 className="font-medium">Sources & Assumptions</h3>
                      {data.domainKnowledge?.sources && (
                        <div className="flex space-x-2">
                          <Button variant="outline" size="sm" onClick={copyKnowledgeSourcesandAssumptions}>
                            <Copy className="h-4 w-4 mr-1" />
                            Copy Sources & Assumptions
                          </Button>
                        </div>
                      )}
                    </div>
                    {data.domainKnowledge?.sources ? (
                      <ScrollArea className={`${styles.markdownPreview} flex-grow min-h-0 h-full nowheel`}>
                        <MarkdownRenderer markdown={data.domainKnowledge.sources} />
                      </ScrollArea>
                    ) : isGathering ? (
                      <div className="h-full flex flex-col items-center justify-center text-center flex-grow min-h-0">
                        <div className="animate-spin mb-4">
                          <RefreshCw className="h-8 w-8 text-gray-400" />
                        </div>
                        <p>Gathering sources and assumptions...</p>
                      </div>
                    ) : (
                      <div className="h-full flex flex-col items-center justify-center text-center flex-grow min-h-0">
                        <FileText className="h-16 w-16 text-gray-300 mb-4" />
                        <p className="text-gray-500">No sources and assumptions available yet</p>
                        <p className="text-sm text-gray-400 mt-2">Gather domain knowledge first</p>
                      </div>
                    )}
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="markdown" className="border-none p-0 mt-2 flex flex-col flex-grow min-h-0">
                <div
                  className="flex flex-col flex-grow min-h-0 space-y-4 scrollable-content nodrag"
                  style={{ overflowY: 'auto', userSelect: 'text' }}
                >
                  <div className={`${styles.markdownPreview} border rounded-md p-3 flex flex-col flex-grow min-h-0`}>
                    <div className="flex justify-between items-center mb-2">
                      <h3 className="font-medium">Markdown</h3>
                      {data.domainKnowledge?.markdown && (
                        <div className={`${styles.markdownPreview} flex space-x-2`}>
                          <Button variant="outline" size="sm" onClick={copyKnowledgeMarkdown}>
                            <Copy className="h-4 w-4 mr-1" />
                            Copy Markdown
                          </Button>
                        </div>
                      )}
                    </div>
                    {data.domainKnowledge?.markdown ? (
                      <ScrollArea className="flex-grow min-h-0 h-full nowheel">
                        <MarkdownRenderer markdown={data.domainKnowledge.markdown} />
                      </ScrollArea>
                    ) : (
                      <div className="text-gray-400 italic p-2">
                        No markdown available yet.
                      </div>
                    )}
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        )}

        <Dialog open={showHelpDialog} onOpenChange={setShowHelpDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Domain Knowledge Help</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p><strong>Domain Knowledge</strong> gathers comprehensive information about specific domains and subdomains.</p>
              <div className="space-y-2">
                <h4 className="font-medium">How to use:</h4>
                <ol className="list-decimal ml-5 space-y-1">
                  <li>Enter the domain you want to analyze (e.g., "Information Technology")</li>
                  <li>Optionally specify a subdomain for more focused analysis</li>
                  <li>Click "Gather Domain Knowledge" to start the analysis</li>
                  <li>Review the structured information in the Domain Information tab</li>
                  <li>Check the Sources & Assumptions tab for detailed context</li>
                </ol>
              </div>
              <div className="space-y-2">
                <h4 className="font-medium">What you'll get:</h4>
                <ul className="list-disc ml-5 space-y-1">
                  <li>Industry classification codes (NAICS/NACE)</li>
                  <li>Domain-specific terminology</li>
                  <li>Data types and processing methods</li>
                  <li>Knowledge transformation processes</li>
                  <li>Applicable standards and frameworks</li>
                  <li>Detailed sources and assumptions</li>
                </ul>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={() => setShowHelpDialog(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Confirm Deletion</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this Domain Knowledge node? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDeleteConfirmed}>Delete</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Node Configuration Dialog */}
        {isConfigDialogVisible && !isLocked && (
          <NodeConfigurationDialog
            visible={isConfigDialogVisible}
            configuration={nodeConfig}
            onSave={(newConfig) => {
              syncNodeData({ configuration: newConfig })
              setIsConfigDialogVisible(false)
            }}
            onHide={() => setIsConfigDialogVisible(false)}
          />
        )}

        {/* DataSettings dialog */}
        {isDataSettingsDialogVisible && (
          <NodeDataSettingsDialog
            visible={isDataSettingsDialogVisible}
            onHide={() => setIsDataSettingsDialogVisible(false)}
            settings={dataSettings}
            nodeId={id}
            onSettingsChange={handleDataSettingsChange}
            // Remove input link handlers, only outgoing/exposed
            forceShowAvailableNodes={true}
            debug={false}
          />
        )}
      </div>
    </NodeBaseContainer>
  )
}

export default memo(NodeDomainKnowledge)

// Add the domain/subdomain list (could be moved to a separate file if desired)
const DOMAIN_LIST = [
  {
    domain: "Information Technology",
    naics: ["51"],
    nace: ["J"],
    subdomains: [
      "Software Development",
      "Game Development",
      "Data Science & Analytics",
      "Cybersecurity",
      "Cloud Infrastructure",
      "DevOps",
      "AI & Machine Learning",
      "Web Development",
      "Mobile App Development",
      "IT Support Services",
      "IT Consulting",
      "Blockchain Development"
    ]
  },
  {
    domain: "Healthcare",
    naics: ["62"],
    nace: ["Q"],
    subdomains: [
      "General Medicine",
      "Nursing",
      "Surgery",
      "Radiology",
      "Psychiatry",
      "Public Health",
      "Medical Devices",
      "Pharmaceuticals",
      "Health IT",
      "Medical Research",
      "Emergency Services"
    ]
  },
  {
    domain: "Industrial & Manufacturing",
    naics: ["21", "22", "23", "31-33"],
    nace: ["B", "C", "D", "F"],
    subdomains: [
      "Automotive",
      "Aerospace",
      "Construction",
      "Civil Engineering",
      "Electrical Engineering",
      "Mechanical Engineering",
      "Robotics",
      "Energy Production",
      "Chemical Processing",
      "Mining",
      "Textile Production",
      "Food & Beverage Manufacturing"
    ]
  },
  {
    domain: "Business & Finance",
    naics: ["52", "53", "54"],
    nace: ["K", "L", "M"],
    subdomains: [
      "Accounting",
      "Auditing",
      "Banking",
      "Financial Services",
      "Insurance",
      "Investment Management",
      "Real Estate",
      "Business Analysis",
      "Corporate Strategy",
      "Human Resources",
      "Sales & Marketing"
    ]
  },
  {
    domain: "Education & Research",
    naics: ["61", "54"],
    nace: ["P", "M"],
    subdomains: [
      "Primary Education",
      "Secondary Education",
      "Higher Education",
      "Educational Technology",
      "Scientific Research",
      "Social Science Research",
      "Online Learning Platforms"
    ]
  },
  {
    domain: "Media & Creative Arts",
    naics: ["51", "71"],
    nace: ["J", "R"],
    subdomains: [
      "Game Design",
      "Film Production",
      "Music Production",
      "Journalism",
      "Graphic Design",
      "Photography",
      "Animation",
      "Content Creation",
      "Publishing",
      "Digital Marketing"
    ]
  },
  {
    domain: "Transportation & Logistics",
    naics: ["48-49"],
    nace: ["H"],
    subdomains: [
      "Shipping & Freight",
      "Aviation",
      "Railways",
      "Public Transportation",
      "Warehousing",
      "Supply Chain Management",
      "Maritime Transport"
    ]
  },
  {
    domain: "Government & Public Services",
    naics: ["92"],
    nace: ["O"],
    subdomains: [
      "Defense",
      "Law Enforcement",
      "Fire & Rescue",
      "Urban Planning",
      "Public Policy",
      "Social Services",
      "Regulatory Bodies"
    ]
  },
  {
    domain: "Legal",
    naics: ["54"],
    nace: ["M"],
    subdomains: [
      "Criminal Law",
      "Civil Law",
      "Corporate Law",
      "Intellectual Property",
      "Legal Tech",
      "Mediation"
    ]
  },
  {
    domain: "Agriculture & Environment",
    naics: ["11", "54"],
    nace: ["A", "E", "M"],
    subdomains: [
      "Crop Farming",
      "Animal Husbandry",
      "Forestry",
      "Fisheries",
      "Environmental Science",
      "Waste Management",
      "Sustainability Consulting"
    ]
  },
  {
    domain: "Tourism & Hospitality",
    naics: ["72", "71"],
    nace: ["I", "R"],
    subdomains: [
      "Hotels & Resorts",
      "Travel Agencies",
      "Tour Operations",
      "Event Planning",
      "Food Services",
      "Leisure & Recreation"
    ]
  },
  {
    domain: "Retail & Consumer Services",
    naics: ["44-45"],
    nace: ["G"],
    subdomains: [
      "E-commerce",
      "Retail Stores",
      "Customer Service",
      "Fashion & Apparel",
      "Consumer Electronics"
    ]
  }
]

// Helper for fuzzy search
function getDomainSuggestions(input: string) {
  if (!input) return DOMAIN_LIST
  const lower = input.toLowerCase()
  return DOMAIN_LIST.filter(d =>
    d.domain.toLowerCase().includes(lower)
  )
}
function getSubdomainSuggestions(domain: string, input: string) {
  const found = DOMAIN_LIST.find(d => d.domain === domain)
  if (!found) return []
  if (!input) return found.subdomains
  const lower = input.toLowerCase()
  return found.subdomains.filter(s => s.toLowerCase().includes(lower))
}
